<script>
import { mapGetters, mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import PageHeader from 'dashboard/components-next/layout/PageHeader.vue';

export default {
  name: 'CustomReports',
  components: {
    PageHeader,
    V4Button,
    ComboBox,
  },
  data() {
    return {
      selectedDashboard: null,
      selectedDashboardId: null,
      currentUrl: null,
      isIframeLoaded: false,
      refreshKey: 0,
      isFullscreen: false,
      _initialized: false,
    };
  },
  computed: {
    ...mapGetters(['getCustomDashboards', 'getCustomDashboardsUIFlags']),

    dashboards() {
      return this.getCustomDashboards || [];
    },

    uiFlags() {
      return this.getCustomDashboardsUIFlags;
    },

    availableDashboards() {
      return this.dashboards.filter(dashboard => dashboard.available);
    },

    dashboardOptions() {
      const options = this.availableDashboards.map(dashboard => ({
        value: dashboard.id,
        label: dashboard.name,
      }));
      return options;
    },

    hasError() {
      return (
        this.uiFlags.error || this.dashboards.some(d => !d.available && d.error)
      );
    },

    errorMessage() {
      if (this.uiFlags.error) return this.uiFlags.error;
      const errorDashboard = this.dashboards.find(d => !d.available && d.error);
      return errorDashboard?.error || 'Erro desconhecido';
    },
  },

  mounted() {
    this.initializeComponent();
  },

  beforeUnmount() {
    this.cleanupComponent();
  },

  deactivated() {
    this.cleanupComponent();
  },

  methods: {
    ...mapActions(['fetchCustomDashboards']),

    initializeComponent() {
      // Evita execução dupla
      if (this._initialized) {
        return;
      }

      this.loadDashboards();
      document.addEventListener('keydown', this.handleEscapeKey);
      this._initialized = true;
    },

    cleanupComponent() {
      document.removeEventListener('keydown', this.handleEscapeKey);
      this._initialized = false;

      // Restaura overflow se estava em fullscreen
      if (this.isFullscreen) {
        document.body.style.overflow = '';
      }
    },

    async loadDashboards() {
      try {
        const dashboards = await this.fetchCustomDashboards();

        // Aguarda um pouco para os dados serem processados pelo Vuex
        await this.$nextTick();

        // Auto-seleciona o primeiro dashboard disponível se não houver seleção
        if (this.availableDashboards.length > 0 && !this.selectedDashboard) {
          this.selectedDashboardId = this.availableDashboards[0].id;
          this.selectDashboard(this.availableDashboards[0]);
        }
      } catch (error) {
        useAlert('Erro ao carregar dashboards personalizados');
      }
    },

    onDashboardSelect() {
      const dashboard = this.availableDashboards.find(
        d => d.id === this.selectedDashboardId
      );
      if (dashboard) {
        this.selectDashboard(dashboard);
      } else {
        console.error(
          'DASHBOARD NÃO ENCONTRADO para o ID:',
          this.selectedDashboardId
        );
      }
    },

    selectDashboard(dashboard) {
      if (!dashboard || !dashboard.available) {
        const errorMsg = dashboard
          ? `Dashboard ${dashboard.name} não está disponível: ${dashboard.error}`
          : 'Dashboard inválido selecionado';
        useAlert(errorMsg);
        return;
      }

      this.selectedDashboard = dashboard;
      this.selectedDashboardId = dashboard.id;
      this.currentUrl = dashboard.url;
      this.isIframeLoaded = false;
      this.refreshKey++; // Force iframe reload
    },

    refreshDashboards() {
      this.loadDashboards();
    },

    refreshCurrentDashboard() {
      if (this.selectedDashboard) {
        const currentId = this.selectedDashboard.id;

        this.loadDashboards().then(() => {
          const updatedDashboard = this.availableDashboards.find(
            d => d.id === currentId
          );
          if (updatedDashboard) {
            this.selectDashboard(updatedDashboard);
          } else {
            useAlert('Dashboard atual não está mais disponível');
          }
        });
      }
    },

    onIframeLoad() {
      this.isIframeLoaded = true;
    },

    onIframeError() {
      useAlert('Erro ao carregar o dashboard. Tente novamente.');
      this.isIframeLoaded = false;
    },

    openInNewTab() {
      if (this.currentUrl) {
        window.open(this.currentUrl, '_blank', 'noopener,noreferrer');
      } else {
        useAlert('Nenhum dashboard selecionado para abrir');
      }
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;

      if (this.isFullscreen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },

    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.isFullscreen) {
        this.toggleFullscreen();
      }
    },
  },

  watch: {
    availableDashboards: {
      handler(newDashboards) {
        if (
          newDashboards.length > 0 &&
          !this.selectedDashboard &&
          this._initialized
        ) {
          this.selectedDashboardId = newDashboards[0].id;
          this.selectDashboard(newDashboards[0]);
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-full px-2 mt-2">
    <!-- Header -->
    <PageHeader
      v-show="!isFullscreen"
      icon="i-lucide-chart-spline"
      accent="teal"
      :title="$t('REPORT.CUSTOM_REPORTS.TITLE')"
      :subtitle="$t('REPORT.CUSTOM_REPORTS.DESCRIPTION')"
    >
      <template #actions>
        <V4Button
          v-if="selectedDashboard"
          :label="$t('REPORT.CUSTOM_REPORTS.REFRESH_DASHBOARD')"
          icon="i-ph-arrows-clockwise"
          size="sm"
          variant="outline"
          @click="refreshDashboards"
        />
      </template>
    </PageHeader>

    <!-- Seletor de Dashboard -->
    <div
      v-show="!isFullscreen && availableDashboards.length > 1"
      class="px-4 py-2 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700"
    >
      <div class="flex items-center gap-4">
        <label
          class="text-sm font-medium text-slate-700 dark:text-slate-300 min-w-fit"
        >
          {{ $t('REPORT.CUSTOM_REPORTS.SELECT_DASHBOARD') }}:
        </label>
        <div class="flex-1 max-w-xs">
          <ComboBox
            v-model="selectedDashboardId"
            :options="dashboardOptions"
            :placeholder="$t('REPORT.CUSTOM_REPORTS.CHOOSE_DASHBOARD')"
            size="sm"
            @update:model-value="onDashboardSelect"
          />
        </div>
      </div>
    </div>

    <!-- Conteúdo Principal -->
    <div
      class="flex-1"
      :class="[
        isFullscreen
          ? 'fixed inset-0 z-50 bg-white dark:bg-slate-900'
          : 'pt-2 px-2',
      ]"
    >
      <!-- Loading State -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center h-full"
      >
        <div class="flex flex-col items-center space-y-4">
          <div
            class="w-8 h-8 border-4 border-woot-500 border-t-transparent rounded-full animate-spin"
          />
          <p class="text-slate-600 dark:text-slate-400">
            {{ $t('REPORT.CUSTOM_REPORTS.LOADING') }}
          </p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="hasError" class="flex items-center justify-center h-full">
        <div class="text-center">
          <div class="w-16 h-16 mx-auto mb-4 text-red-500">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <h3
            class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2"
          >
            {{ $t('REPORT.CUSTOM_REPORTS.ERROR_TITLE') }}
          </h3>
          <p class="text-slate-500 dark:text-slate-400 mb-4">
            {{ errorMessage }}
          </p>
          <V4Button
            :label="$t('REPORT.CUSTOM_REPORTS.TRY_AGAIN')"
            icon="i-ph-arrow-clockwise"
            @click="refreshDashboards"
          />
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="availableDashboards.length === 0"
        class="flex items-center justify-center h-full"
      >
        <div class="text-center">
          <div class="w-16 h-16 mx-auto mb-4 text-slate-400">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path
                d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"
              />
            </svg>
          </div>
          <h3
            class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2"
          >
            {{ $t('REPORT.CUSTOM_REPORTS.NO_DASHBOARDS_TITLE') }}
          </h3>
          <p class="text-slate-500 dark:text-slate-400 mb-4">
            {{ $t('REPORT.CUSTOM_REPORTS.NO_DASHBOARDS_DESCRIPTION') }}
          </p>
          <V4Button
            :label="$t('REPORT.CUSTOM_REPORTS.REFRESH')"
            icon="i-ph-arrow-clockwise"
            @click="refreshDashboards"
          />
        </div>
      </div>

      <!-- Dashboard Content -->
      <div
        v-else-if="currentUrl"
        class="flex flex-col w-full overflow-hidden"
        :class="[
          isFullscreen
            ? 'h-full bg-white dark:bg-slate-900'
            : 'h-[calc(100vh-12rem)] rounded-lg bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700',
        ]"
      >
        <!-- Toolbar acima do iframe -->
        <div
          v-if="isIframeLoaded"
          class="flex items-center justify-end gap-2 px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 shrink-0"
        >
          <!-- ComboBox em fullscreen -->
          <div
            v-if="isFullscreen && availableDashboards.length > 1"
            class="mr-auto"
          >
            <ComboBox
              v-model="selectedDashboardId"
              :options="dashboardOptions"
              :placeholder="$t('REPORT.CUSTOM_REPORTS.CHOOSE_DASHBOARD')"
              size="sm"
              class="min-w-80"
              @update:model-value="onDashboardSelect"
            />
          </div>

          <!-- Botão Fullscreen -->
          <button
            :title="isFullscreen ? 'Sair da tela cheia (ESC)' : 'Tela cheia'"
            class="flex items-center justify-center w-9 h-9 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
            @click="toggleFullscreen"
          >
            <svg
              v-if="!isFullscreen"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="text-slate-600 dark:text-slate-400"
            >
              <path
                d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"
              />
            </svg>
            <svg
              v-else
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="text-slate-600 dark:text-slate-400"
            >
              <path
                d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"
              />
            </svg>
          </button>
        </div>

        <!-- Loading Overlay -->
        <div
          v-if="!isIframeLoaded"
          class="flex-1 flex items-center justify-center bg-white dark:bg-slate-900"
        >
          <div class="flex flex-col items-center space-y-4">
            <div
              class="w-8 h-8 border-4 border-woot-500 border-t-transparent rounded-full animate-spin"
            />
            <p class="text-slate-600 dark:text-slate-400">
              {{ $t('REPORT.CUSTOM_REPORTS.LOADING_DASHBOARD') }}
            </p>
            <p
              v-if="selectedDashboard"
              class="text-sm text-slate-500 dark:text-slate-500"
            >
              {{ selectedDashboard.name }}
            </p>
          </div>
        </div>

        <!-- Iframe -->
        <iframe
          :key="refreshKey"
          :src="currentUrl"
          class="w-full flex-1 border-0 bg-white"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads allow-popups-to-escape-sandbox"
          allow="downloads"
          @load="onIframeLoad"
          @error="onIframeError"
        />
      </div>

      <!-- No Dashboard Selected -->
      <div v-else class="flex items-center justify-center h-full">
        <div class="text-center">
          <div class="w-16 h-16 mx-auto mb-4 text-slate-400">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path
                d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"
              />
            </svg>
          </div>
          <h3
            class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2"
          >
            {{ $t('REPORT.CUSTOM_REPORTS.SELECT_DASHBOARD_TITLE') }}
          </h3>
          <p class="text-slate-500 dark:text-slate-400">
            {{ $t('REPORT.CUSTOM_REPORTS.SELECT_DASHBOARD_DESCRIPTION') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
